import React from 'react';
import styled from 'styled-components';
import {
  Avatar,
  Confirmation,
  Currency,
  DateTime,
  InternalClientTooltip,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  MemberContactPopover,
  ProjectAdmins,
  ProjectHealthScoreBar,
  ProjectLink,
  Tooltip,
} from '~/components';
import {
  useApi,
  useConfirmation,
  useIntegrations,
  useSession,
  useSubscription,
  useToast,
  useWorkspace,
} from '~/contexts';
import { useAuth, useFeatures } from '~/hooks';
import { colors } from '~/styles';
import { QueryString } from '~/utils';
import ArchiveProjectConfirmation from './ArchiveProjectConfirmation';
import QBOGrey from './assets/qbo-grey.svg?react';
import QBOIndicator from './QBOIndicator';
import UseRolesProjectConfirmation from './UseRolesProjectConfirmation';
import LockIcon from '~/components/LockIcon';

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Status = styled.span`
  color: ${({ status }) =>
    ({
      tentative: colors.grey25,
      not_started: colors.warning,
      in_progress: colors.success,
      paused: colors.danger,
      completed: colors.black,
      cancelled: colors.grey55,
    })[status]};
`;

const Owner = styled.div`
  display: flex;
  align-items: center;
`;

const OwnerInfo = styled.span`
  margin-left: 0.5rem;
`;

function ProjectsTable({
  data,
  params,
  mode = 'page',
  action,
  columns,
  selection,
  showBulkSelection,
  onEdit,
  onClone,
  onSort,
  onDelete,
  onRowClick,
  onSaved,
  onTrackTime,
  onLoadMore,
}) {
  const projects = data.results;
  const auth = useAuth();
  const api = useApi();
  const { isAdmin: isSysAdmin } = useSession();

  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const { notify } = useSubscription();
  const features = useFeatures();
  const integrations = useIntegrations();

  return (
    <ListView>
      <ListView.Header>
        <ListViewSelection.Column
          selection={selection}
          disabled={data.results.length === 0 || action === 'filter'}
          isVisible={mode === 'page' && showBulkSelection}
        />
        <ListView.Column sticky width="3rem" isVisible={integrations.qbo}>
          <QBOGrey style={{ width: '1rem', height: '1rem' }} />
        </ListView.Column>

        <ListView.Column sticky width="3rem">
          <LockIcon />
        </ListView.Column>
        <ListView.Column sticky minWidth="16rem" name="name" onSort={onSort} sort={params.sort}>
          Name
        </ListView.Column>
        <ListView.Column
          name="code"
          minWidth="16rem"
          onSort={onSort}
          sort={params.sort}
          isVisible={columns.visible.code}>
          Code
        </ListView.Column>
        <ListView.Column
          name="statusId"
          width="7.5rem"
          onSort={onSort}
          sort={params.sort}
          isVisible={columns.visible.status}>
          Status
        </ListView.Column>
        <ListView.Column minWidth="15rem" isVisible={columns.visible.projectAdmin}>
          Admins
        </ListView.Column>
        <ListView.Column
          width="9.75rem"
          name="billingTypeId"
          onSort={onSort}
          sort={params.sort}
          isVisible={columns.visible.billingType}>
          Billing Type
        </ListView.Column>
        <ListView.Column
          width="15rem"
          name="practice.name"
          onSort={onSort}
          sort={params.sort}
          isVisible={features.practices && columns.visible.practice}>
          Practice
        </ListView.Column>
        <ListView.Column
          width="9.75rem"
          name="businessUnit.name"
          onSort={onSort}
          sort={params.sort}
          isVisible={features.businessUnits && columns.visible.businessUnit}>
          Business Unit
        </ListView.Column>
        <ListView.Column
          name="salesRepresentative.name"
          width="15rem"
          onSort={onSort}
          sort={params.sort}
          isVisible={columns.visible.salesRepresentative}>
          Sales Representative
        </ListView.Column>
        <ListView.Column
          width="15rem"
          name="projectType.name"
          onSort={onSort}
          sort={params.sort}
          isVisible={columns.visible.projectType}>
          Type
        </ListView.Column>
        <ListView.Column
          width="9.75rem"
          name="start"
          onSort={onSort}
          sort={params.sort}
          align="right"
          isVisible={columns.visible.start}>
          Start
        </ListView.Column>
        <ListView.Column
          width="9.75rem"
          name="end"
          onSort={onSort}
          sort={params.sort}
          align="right"
          isVisible={columns.visible.end}>
          End
        </ListView.Column>
        <ListView.Column
          width="9.75rem"
          name="currency"
          onSort={onSort}
          sort={params.sort}
          isVisible={columns.visible.currency}>
          Currency
        </ListView.Column>
        <ListView.Column
          align="center"
          width="7.5rem"
          name="healthScore"
          onSort={onSort}
          sort={params.sort}
          isVisible={features.projectHealth && auth.projects.viewHealth && columns.visible.health}>
          Health
        </ListView.Column>
        <ListView.Column
          width="7.5rem"
          align="right"
          name="createdAt"
          onSort={onSort}
          sort={params.sort}
          isVisible={columns.visible.createdAt}>
          Created
        </ListView.Column>
        <ListViewActions.Column isVisible={mode === 'page'} />
      </ListView.Header>

      <ListView.Body fade={action === 'filter'}>
        {!data.total && <ListView.Empty />}

        {projects.map((project) => {
          const allowCreate = auth.projects.create;
          const viewHealth = features.projectHealth && project.permissions.viewHealth && project.healthScore;

          async function handleSetRecordStatus({ recordStatusId, statusId }) {
            try {
              const { data } = await api.www
                .workspaces(workspace.id)
                .projects(project.id)
                .setRecordStatus({ recordStatusId, statusId });
              await onSaved(data);
            } catch ({ message }) {
              toast.error(message);
            }
          }

          async function handleSetUseRoles({ useRoles }) {
            try {
              const { data } = await api.www.workspaces(workspace.id).projects(project.id).setUseRoles({ useRoles });
              await onSaved(data);
            } catch ({ message }) {
              toast.error(message);
            }
          }

          const handleArchive = async () => {
            const confirm = await confirmation.prompt((resolve) => (
              <ArchiveProjectConfirmation
                project={project}
                onResolve={resolve}
                onSubmit={(shouldUpdateStatus) =>
                  handleSetRecordStatus({
                    recordStatusId: 'archived',
                    statusId: shouldUpdateStatus ? 'completed' : undefined,
                  })
                }
              />
            ));
            if (!confirm) return;

            notify(useSubscription.keys.refresh_timer);
          };

          const handleUseRolesConvert = async () => {
            const confirm = await confirmation.prompt((resolve) => (
              <UseRolesProjectConfirmation
                project={project}
                onResolve={resolve}
                onSubmit={() =>
                  handleSetUseRoles({
                    useRoles: true,
                  })
                }
              />
            ));
            if (!confirm) return;

            notify(useSubscription.keys.refresh_timer);
          };

          const handleUnarchive = async () => {
            const confirm = await confirmation.prompt((resolve) => (
              <Confirmation resolve={resolve}>
                {project.client.recordStatusId === 'archived'
                  ? 'The client associated with this project is currently archived. If you unarchive this project, it will also unarchive the client.'
                  : 'Are you sure that you want to unarchive this project?'}
              </Confirmation>
            ));

            if (!confirm) return;

            handleSetRecordStatus({ recordStatusId: 'active' });
          };

          return (
            <ListView.Row
              key={project.id}
              isDisabled={project.recordStatus.id === 'archived'}
              onClick={() => onRowClick(project)}>
              <ListViewSelection.Cell id={project.id} selection={selection} />
              <ListView.Cell>
                {project.qboConnectionName && <QBOIndicator qboConnectionName={project.qboConnectionName} />}
              </ListView.Cell>

              <ListView.Cell>
                {project.lockTimeAndExpenses && (
                  <Tooltip message="Time and expenses have been locked for this project." placement="right">
                    <LockIcon />
                  </Tooltip>
                )}
              </ListView.Cell>
              <ListView.Cell>
                <ProjectInfo>
                  <div style={{ display: 'flex' }}>
                    <ProjectLink project={project} onClick={(e) => e.stopPropagation()} />
                  </div>
                  <small>
                    {project.client.name}
                    {project.client.isInternal && <InternalClientTooltip />}
                  </small>
                </ProjectInfo>
              </ListView.Cell>
              <ListView.Cell style={{ wordBreak: 'break-word' }}>{project.code}</ListView.Cell>
              <ListView.Cell>
                <Status status={project.status.id}>{project.status.name}</Status>
              </ListView.Cell>
              <ListView.Cell>
                <ProjectAdmins project={project} />
              </ListView.Cell>
              <ListView.Cell>
                <ProjectInfo>
                  <p>{project.billingType.name}</p>
                  {auth.projects.viewRevenue && project.isBillable && <Budget project={project} />}
                </ProjectInfo>
              </ListView.Cell>
              <ListView.Cell>{project.practice?.name}</ListView.Cell>
              <ListView.Cell>{project.businessUnit?.name}</ListView.Cell>
              <ListView.Cell>
                {project.salesRepresentative && (
                  <Owner>
                    <MemberContactPopover member={project.salesRepresentative} placement="left">
                      <Avatar value={project.salesRepresentative} isCircle hasBackground initialsFontSize=".9rem" />
                    </MemberContactPopover>
                    <OwnerInfo>{project.salesRepresentative.name}</OwnerInfo>
                  </Owner>
                )}
              </ListView.Cell>
              <ListView.Cell>{project.projectType?.name}</ListView.Cell>
              <ListView.Cell>
                <DateTime value={project.start} />
              </ListView.Cell>
              <ListView.Cell>
                <DateTime value={project.end} />
              </ListView.Cell>
              <ListView.Cell>{project.currency}</ListView.Cell>
              <ListView.Cell data-testid="health_cell">
                {viewHealth && <ProjectHealthScoreBar score={project.healthScore} />}
              </ListView.Cell>
              <ListView.Cell>
                <DateTime value={project.createdAt} />
              </ListView.Cell>

              <ListViewActions isVisible={mode === 'page'}>
                <ListViewActions.View onClick={() => onRowClick(project)} />

                <hr />

                <ListViewMenu>
                  {({ setIsOpen }) => {
                    const handleAction = (action) => setIsOpen(false) || action;

                    return (
                      <>
                        <ListViewMenu.Item onClick={() => handleAction(onRowClick(project))}>View</ListViewMenu.Item>

                        <ListViewMenu.Item
                          disabled={!project.permissions.edit}
                          tooltip={
                            !project.permissions.edit ? 'Insufficient permissions to edit this project.' : undefined
                          }
                          onClick={() => handleAction(onEdit(project))}>
                          Edit
                        </ListViewMenu.Item>

                        <ListViewMenu.Link
                          to={`/app/${workspace.key}/reports/time-entries?${new QueryString({
                            start: 'not_set',
                            end: 'not_set',
                            project: project.id,
                          })}`}>
                          View Time Entries
                        </ListViewMenu.Link>

                        <ListViewMenu.Item
                          disabled={!allowCreate}
                          tooltip={!allowCreate ? 'Insufficient permissions to clone this project.' : undefined}
                          onClick={() => handleAction(onClone(project))}>
                          Clone
                        </ListViewMenu.Item>

                        {project.useRoles === false && isSysAdmin ? (
                          <ListViewMenu.Item
                            disabled={!project.permissions.edit}
                            tooltip={
                              !project.permissions.edit
                                ? 'Insufficient permissions to convert this project.'
                                : undefined
                            }
                            onClick={() => handleAction(handleUseRolesConvert())}>
                            Use Roles
                          </ListViewMenu.Item>
                        ) : (
                          <></>
                        )}

                        {project.recordStatus.id === 'active' ? (
                          <ListViewMenu.Item
                            disabled={!project.permissions.delete}
                            tooltip={
                              !project.permissions.delete
                                ? 'Insufficient permissions to archive this project.'
                                : undefined
                            }
                            onClick={() => handleAction(handleArchive())}>
                            Archive
                          </ListViewMenu.Item>
                        ) : (
                          <ListViewMenu.Item
                            disabled={!project.permissions.delete}
                            tooltip={
                              !project.permissions.delete
                                ? 'Insufficient permissions to unarchive this project.'
                                : undefined
                            }
                            onClick={() => handleAction(handleUnarchive())}>
                            Unarchive
                          </ListViewMenu.Item>
                        )}

                        <ListViewMenu.Item
                          onClick={() => onTrackTime({ project })}
                          disabled={
                            !project.isProjectMember ||
                            project.recordStatusId !== 'active' ||
                            project.lockTimeAndExpenses
                          }
                          tooltip={(() => {
                            if (!project.isProjectMember)
                              return 'In order to track time to this project, you must be an active project team member.';
                            if (project.recordStatusId !== 'active') return 'This project is archived.';
                            if (project.lockTimeAndExpenses)
                              return 'This project does not allow adding new time entries.';
                          })()}>
                          Track Time
                        </ListViewMenu.Item>

                        <ListViewMenu.DeleteItem
                          disabled={!project.permissions.delete}
                          tooltip={
                            !project.permissions.delete
                              ? 'Insufficient permissions to delete this project.'
                              : 'This project is associated with one or more invoices, credit notes, or opportunities and cannot be deleted.'
                          }
                          onCheckDependencies={async (workspace) =>
                            project.permissions.delete &&
                            (await workspace.projects(project.id).hasBlockingDependencies()).data
                          }
                          onClick={() => handleAction(onDelete(project))}>
                          Delete
                        </ListViewMenu.DeleteItem>
                      </>
                    );
                  }}
                </ListViewMenu>
              </ListViewActions>
            </ListView.Row>
          );
        })}

        {data.total > data.results.length && <ListView.Loader key={data.results.length} onIntersecting={onLoadMore} />}
      </ListView.Body>

      <ListView.Status total={data.total} label="Project" isLoading={!!action} />
    </ListView>
  );
}

function Budget({ project }) {
  if (!project.permissions.viewRevenue) return null;

  const currency = project.currency;

  if (project.useBudget)
    return (
      <p>
        <small>
          <Currency
            value={project.budget?.revenue}
            minimumFractionDigits={0}
            maximumFractionDigits={0}
            currency={currency}
          />
        </small>
      </p>
    );

  if (project.useMonthlyBudget)
    return (
      <p>
        <small>
          <Currency
            value={project.monthlyBudget?.revenue}
            minimumFractionDigits={0}
            maximumFractionDigits={0}
            currency={currency}
          />{' '}
          per month
        </small>
      </p>
    );

  return null;
}

export default ProjectsTable;
